// Customer directory. Every role that holds the Customers view, which today
// means both of them.
//
// Visibility is global. A franchisee sees the same list as an admin, not only
// the accounts they service, so nothing on this page is filtered by who is
// looking. Adding and editing needs the write grant, which is why the button is
// conditional and the server refuses the route either way.
//
// The two figure columns are rollups computed from activity records, never
// typed by anyone. See server/rollups.js.
//
// No status column: a customer in the directory is taken to be active. The
// protocol is one button per row that opens the whole protocol in a dialog,
// rather than a column per answer. Last test is for staff: green when work
// is known within the last 30 days, red otherwise, from the activity lines.

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { listCustomers } from '../api.js'
import { useAuth } from '../auth.jsx'
import {
  Button,
  ButtonLink,
  Dialog,
  EmptyState,
  Input,
  Notice,
  Panel,
  Pill,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '../components/ui/index.js'
import { ACTION } from '../../../shared/permissions.js'
import { ProtocolRows } from '../protocol.jsx'
import { formatMoney, formatCount, formatDate, formatTenure, daysSince } from '../vocab.js'

const RECENT_DAYS = 30

// The last known test, colored against the window: green inside it, red
// outside it or when nothing has ever been recorded.
function LastTest({ date }) {
  const days = daysSince(date)
  if (days === null) return <Pill tone="bad">None yet</Pill>
  return (
    <>
      <Pill tone={days <= RECENT_DAYS ? 'ok' : 'bad'}>{formatDate(date)}</Pill>
      <div className="ui-cell-meta">
        {days === 0 ? 'Today' : days === 1 ? '1 day ago' : `${days} days ago`}
      </div>
    </>
  )
}

const EMPTY_FILTERS = { q: '' }

// The whole protocol for one customer, read-only and in front of you. The
// profile holds everything else; the dialog links there.
function ProtocolDialog({ customer, onClose }) {
  return (
    <Dialog
      title={`${customer.name} protocol`}
      onClose={onClose}
      width={560}
      footer={
        <>
          <ButtonLink to={`/customers/${customer.id}`}>Open customer</ButtonLink>
          <span className="spacer" />
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      <ProtocolRows customer={customer} />
    </Dialog>
  )
}

export default function Customers() {
  const { can } = useAuth()
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [customers, setCustomers] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [protocolFor, setProtocolFor] = useState(null)

  const canWrite = can(ACTION.CUSTOMERS_WRITE)
  const canReadRecency = can(ACTION.ISSUES_READ)
  // FTFS revenue (customer bills) is nested under rollups.ftfs only for a
  // viewer who may read it; the columns follow the data.
  const canReadBills = can(ACTION.CUSTOMER_BILLS_READ)

  const load = useCallback(async (active) => {
    try {
      const data = await listCustomers(active)
      setCustomers(data.customers)
      setLoadError(null)
    } catch (caught) {
      setLoadError(caught.message)
    }
  }, [])

  // Debounced so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => load(filters), 200)
    return () => clearTimeout(timer)
  }, [filters, load])

  function change(field, value) {
    setFilters((current) => ({ ...current, [field]: value }))
  }

  const isFiltered = Object.values(filters).some(Boolean)

  if (loadError) {
    return <Notice tone="bad">{loadError}</Notice>
  }

  return (
    <>
      <div className="toolbar">
        <Input
          type="search"
          value={filters.q}
          onChange={(event) => change('q', event.target.value)}
          placeholder="Search name or entity"
          aria-label="Search customers"
          className="toolbar-search"
        />
        {isFiltered ? (
          <Button variant="ghost" onClick={() => setFilters(EMPTY_FILTERS)}>
            Clear
          </Button>
        ) : null}
        <span className="spacer" />
        {canWrite ? (
          <ButtonLink variant="primary" to="/customers/new">
            Add customer
          </ButtonLink>
        ) : null}
      </div>

      <Panel title="Directory" support={customers ? `${customers.length} shown` : 'Loading'} flush>
        {customers && customers.length === 0 ? (
          <EmptyState title={isFiltered ? 'Nothing matches that search' : 'No customers yet'}>
            {isFiltered
              ? 'Widen the search or clear it.'
              : canWrite
                ? 'The real National Accounts customers have not been identified yet. Add the first one and its revenue and test totals build themselves from recorded activity.'
                : 'The real National Accounts customers have not been identified yet.'}
          </EmptyState>
        ) : null}

        {customers && customers.length > 0 ? (
          <Table>
            <Thead>
              <Tr>
                <Th>Customer</Th>
                <Th>Customer since</Th>
                {canReadRecency ? <Th>Last test</Th> : null}
                <Th>Protocol</Th>
                {canReadBills ? (
                  <>
                    <Th numeric>FTFS this year</Th>
                    <Th numeric>FTFS lifetime</Th>
                    <Th numeric>FTFS tests</Th>
                    <Th numeric>Owner this year</Th>
                    <Th numeric>Owner lifetime</Th>
                  </>
                ) : (
                  <>
                    <Th numeric>Spend this year</Th>
                    <Th numeric>Lifetime revenue</Th>
                    <Th numeric>Lifetime tests</Th>
                  </>
                )}
              </Tr>
            </Thead>
            <Tbody>
              {customers.map((customer) => (
                <Tr key={customer.id}>
                  <Td>
                    <Link to={`/customers/${customer.id}`}>{customer.name}</Link>
                    {customer.isTpa ? (
                      <>
                        {' '}
                        <Pill tone="neutral">TPA</Pill>
                      </>
                    ) : null}
                    {customer.legalEntity ? (
                      <div className="ui-cell-meta">{customer.legalEntity}</div>
                    ) : null}
                  </Td>
                  <Td>
                    {customer.customerSince ? (
                      <>
                        {formatTenure(customer.customerSince)}
                        <div className="ui-cell-meta">{formatDate(customer.customerSince)}</div>
                      </>
                    ) : (
                      <span className="muted">Not set</span>
                    )}
                  </Td>
                  {canReadRecency ? (
                    <Td>
                      <LastTest date={customer.rollups.lastServiceDate} />
                    </Td>
                  ) : null}
                  <Td>
                    <Button
                      onClick={() => setProtocolFor(customer)}
                      aria-label={`Protocol for ${customer.name}`}
                    >
                      Protocol
                    </Button>
                  </Td>
                  {canReadBills && customer.rollups.ftfs ? (
                    <>
                      <Td numeric>{formatMoney(customer.rollups.ftfs.ytdRevenue)}</Td>
                      <Td numeric>{formatMoney(customer.rollups.ftfs.lifetimeRevenue)}</Td>
                      <Td numeric>{formatCount(customer.rollups.ftfs.lifetimeTests)}</Td>
                      <Td numeric>{formatMoney(customer.rollups.ytdRevenue)}</Td>
                      <Td numeric>{formatMoney(customer.rollups.lifetimeRevenue)}</Td>
                    </>
                  ) : (
                    <>
                      <Td numeric>{formatMoney(customer.rollups.ytdRevenue)}</Td>
                      <Td numeric>{formatMoney(customer.rollups.lifetimeRevenue)}</Td>
                      <Td numeric>{formatCount(customer.rollups.lifetimeTests)}</Td>
                    </>
                  )}
                </Tr>
              ))}
            </Tbody>
          </Table>
        ) : null}
      </Panel>

      {protocolFor ? (
        <ProtocolDialog customer={protocolFor} onClose={() => setProtocolFor(null)} />
      ) : null}
    </>
  )
}
